import {
  useRef,
  useMemo,
  useState,
  useTransition,
  useEffect,
  memo,
  BaseSyntheticEvent,
  Fragment,
  ReactElement,
  ReactNode,
} from "react";
import { t } from "@lingui/core/macro";
import { type ColumnDef } from "@tanstack/react-table";
import { Temporal } from "temporal-polyfill";
import { Control, Controller, useForm, UseFormSetValue } from "react-hook-form";
import { symmetricDifference, intersection } from "ramda";

import { TextField } from "_/components/text-field";
import { Select } from "_/components/select";
import { Table } from "_/components/table";
import { Button, ButtonGroup } from "_/components/button";
import {
  Machine,
  LogLevel,
  LOG_LEVEL_NAMES,
  LogEvent,
  LogQuery,
  useMachineLogs,
} from "_/data/machines";
import { Icon } from "_/components/icon";
import { Form } from "_/components/form";
import { useSearchParams } from "_/components/router";
import { DateTime } from "_/components/date-time";

import {
  type DisplayTimeZone,
  dateTimeString,
  timeZoneId,
} from "_/utils/datetime";

import { i18n } from "_/i18n";
import { type Uuid } from "_/types";

import { LogChart, LogLevelCounts } from "./log-chart";
import { openPicker, RangeInputs } from "./range-inputs";
import { ShareLinkButton } from "./share-link-button";

import * as S from "./styled";

type MachineLogsProps = {
  machine: Machine;
};

type ColumnRenderProps = {
  event: LogEvent;
  addFilter: (filterText: string) => void;
};

// Constructs translated label for event levels.
function translatedLabel(level: string): string {
  return t`common.log-levels ${level}`;
}

// Helper for rendering the log level in each table row.
const RenderLevel = ({ event }: ColumnRenderProps) => (
  <S.LevelIndicator $level={event.level} />
);

const MAX_INLINE_CHARS = 500;
const MAX_FILTER_CHARS = 100;

function prepareDisplay(value: unknown, expanded: boolean) {
  const isString = typeof value === "string";
  let raw: string;

  // Safely serialize non-string values
  if (isString) {
    raw = value;
  } else {
    try {
      raw = JSON.stringify(value);
    } catch (err) {
      console.error("prepareDisplay: failed to stringify value", err, value);
      raw = "[unserializable value]";
    }
  }

  const isOver = raw.length > MAX_INLINE_CHARS;
  const shownRaw =
    isOver && !expanded ? `${raw.slice(0, MAX_INLINE_CHARS)}…` : raw;
  const shown = isString ? JSON.stringify(shownRaw) : shownRaw;
  const filterValue = isString ? JSON.stringify(raw) : raw;
  const isFilterable = raw.length <= MAX_FILTER_CHARS;

  return { shown, isOver, isFilterable, filterValue };
}

const RenderDataValue = ({
  label,
  value,
  addFilter,
}: {
  label?: string;
  value: unknown;
  addFilter: (filterText: string) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const { shown, isOver, isFilterable, filterValue } = useMemo(
    () => prepareDisplay(value, expanded),
    [value, expanded]
  );
  const content = (
    <>
      {label ? `${label}: ${shown}` : shown}
      {isOver && (
        <S.SeeMoreLink
          href="#"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation?.();
            setExpanded(!expanded);
          }}
          role="button"
        >
          {expanded ? t`common.see-less` : t`common.see-more`}
        </S.SeeMoreLink>
      )}
    </>
  );

  if (isFilterable) {
    const filter =
      label != null
        ? `[data=$.${label}==${filterValue}]`
        : `[data=$==${filterValue}]`;
    return (
      <S.LogEventDataLine>
        <FilterLink filter={filter} addFilter={addFilter}>
          {content}
        </FilterLink>
      </S.LogEventDataLine>
    );
  }

  return <S.LogEventDataLine>{content}</S.LogEventDataLine>;
};

// Helper for rendering event data in each table row.
const RenderData = ({ event, addFilter }: ColumnRenderProps) => {
  let data = event.data;
  if (!data) {
    return "";
  }

  if (typeof data !== "object") {
    return <RenderDataValue value={data} addFilter={addFilter} />;
  }

  // Extract the `code` property from the data object, if it exists. This is
  // used to display a more user-friendly message in the UI and to make it
  // easier for users to filter log searches by a unique string. Later, `code`
  // can be used as a localization key for generating localized messages if we
  // decide to show some subset of logs to non-internal users.
  if ("code" in data) {
    let _code;
    ({ code: _code, ...data } = data);
  }

  // Serialize the data object space-efficiently (relative to JSON.stringify):
  // * No start/end brackets
  // * No top-level indents
  // * No quotes around top-level keys
  // Nested objects (likely rare) will be serialized normally as JSON.
  return (
    <div>
      {Object.entries(data).map(([key, value]) => (
        <RenderDataValue
          key={key}
          label={key}
          value={value}
          addFilter={addFilter}
        />
      ))}
    </div>
  );
};

const RenderTimestamp = ({
  event,
  displayTimeZone,
}: {
  event: LogEvent;
  displayTimeZone: DisplayTimeZone;
}) => {
  const epochMs = event.timestamp / 1_000;
  // Hovering shows the time in the other time zone, as the column header says.
  const hoverText =
    displayTimeZone === "utc"
      ? dateTimeString({ value: epochMs, precision: "millisecond" })
      : new Date(epochMs).toISOString();

  return (
    <span title={hoverText}>
      <DateTime
        value={epochMs}
        precision="millisecond"
        timeZone={displayTimeZone}
      />
    </span>
  );
};

const RenderMessage = ({ event, addFilter }: ColumnRenderProps) => {
  let { message } = event;
  const { data } = event;
  let code = null;

  // If the message has a `code` in its `data`, strip the code from the message
  // if present so we can display the code as a filter link below.
  if (data && typeof data === "object" && "code" in data) {
    ({ code } = data);
    code = String(code);
    if (message.startsWith(`${code}:`)) {
      message = message.slice(`${code}:`.length).trim();
    }
  }

  // Now, display the code in a fixed-width font to more clearly separate it
  // from the rest of the message. DO NOT REMOVE the wrapper span below, as it
  // is required to make the code display inline with the rest of the message.
  // This is needed because the top-level children of a flex or grid container
  // are forced to be block elements.
  return code ? (
    <span>
      <S.LogEventCode>
        <FilterLink filter={`[code=${code}]`} addFilter={addFilter}>
          {code}
        </FilterLink>
      </S.LogEventCode>
      : {message}
    </span>
  ) : (
    message
  );
};

const RenderSource = ({ event, addFilter }: ColumnRenderProps) => {
  const { component } = event;

  // allow line-breaking in the component name at any dot
  const parts = component.split(".");
  return (
    <FilterLink
      filter={`[source=${component.toLowerCase()}]`}
      addFilter={addFilter}
    >
      <span>
        {parts.map((part, i) => (
          <Fragment key={i}>
            {i > 0 && <wbr />}
            {i === 0 ? part : `.${part}`}
          </Fragment>
        ))}
      </span>
    </FilterLink>
  );
};

const ClearTextButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <S.ClearButton
      iconOnly
      type="button"
      size="small"
      variant="text"
      kind="secondary"
      onClick={onClick}
    >
      <Icon variant="Close" />
    </S.ClearButton>
  );
};

// Preset time ranges, as ISO 8601 durations counted back from the current
// time. They're written into `[since=...]` filters as-is, so each must be in
// the canonical form that `Temporal.Duration#toString` produces. The 24 hour
// range is `PT24H` rather than `P1D` so that it's labelled "24 hours".
const TIME_RANGES = ["PT30M", "PT1H", "PT6H", "PT24H", "P7D", "P14D"] as const;
type TimeRange = (typeof TIME_RANGES)[number];

// Range that double-clicking the chart resets to: the longest preset.
const RESET_TIME_RANGE: TimeRange = "P14D";

// Localized label like "30 minutes" or "7 days". `toLocaleString` uses
// `Intl.DurationFormat`, and shows the ISO 8601 duration (e.g. "PT30M") in
// browsers that don't support it yet.
function timeRangeLabel(range: TimeRange): string {
  return Temporal.Duration.from(range).toLocaleString(i18n.locale, {
    style: "long",
  });
}

function isTimeRange(value: string | null): value is TimeRange {
  return TIME_RANGES.some((range) => range === value);
}

// Safety cap on the number of events fetched for a time range, so that very
// noisy machines don't overwhelm the browser.
const MAX_LOG_EVENTS = 10_000;

// Order in which levels are listed in the chart and counts.
const LEVEL_DISPLAY_ORDER: LogLevel[] = ["error", "warn", "info", "debug"];

type QueryParams = {
  levels: LogLevel[];
  text: string;
  range: TimeRange;
};

const FilterBar = ({
  control,
  setValue,
  filterTextRange,
  onSubmit,
  onRangeChange,
  onCustomRange,
}: {
  control: Control<QueryParams>;
  setValue: UseFormSetValue<QueryParams>;
  /** Range option described by the submitted filter text, if any. */
  filterTextRange: TimeRange | "custom" | null;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  onRangeChange: (range: TimeRange) => void;
  onCustomRange: () => void;
}) => {
  const levelOptions: {
    value: LogLevel | "all";
    label: string;
    labelJsx: ReactNode;
  }[] = [
    {
      value: "all",
      label: t`common.all`,
      labelJsx: <>{t`common.all`}</>,
    },
    ...LOG_LEVEL_NAMES.map((level) => ({
      value: level,
      label: translatedLabel(level),
      labelJsx: (
        <S.LevelOption>
          <S.LevelIndicator $level={level} />
          {translatedLabel(level)}
        </S.LevelOption>
      ),
    })),
  ];

  const presetRangeOptions: { value: TimeRange | "custom"; label: string }[] =
    TIME_RANGES.map((range) => ({
      value: range,
      label: timeRangeLabel(range),
    }));
  const customRangeOption = {
    value: "custom" as const,
    label: t`components.machine-logs.custom-range`,
  };
  const rangeOptions = [...presetRangeOptions, customRangeOption];

  return (
    <Form onSubmit={onSubmit}>
      <S.FilterBar>
        <S.FilterBarItem>
          <S.Label>{t`common.level`}:</S.Label>
          <Controller
            control={control}
            name="levels"
            render={({ field, fieldState }) => {
              return (
                <Select
                  {...field}
                  options={levelOptions}
                  isClearable={false}
                  multiple
                  onChange={(vs: Array<LogLevel | "all">) => {
                    const levels = vs.includes("all")
                      ? LOG_LEVEL_NAMES
                      : vs.filter((v) => v !== "all");
                    field.onChange(levels);
                    void onSubmit();
                  }}
                  invalid={fieldState.invalid}
                  hint={fieldState.error?.message}
                  isSearchable={false}
                />
              );
            }}
          />
        </S.FilterBarItem>
        <S.GrowingFilterBarItem>
          <S.Label>{t`common.search`}:</S.Label>
          <Controller
            control={control}
            name="text"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                type="text"
                invalid={fieldState.invalid}
                hint={fieldState.error?.message}
                iconRight={
                  field.value === "all" ? undefined : (
                    <ClearTextButton
                      onClick={() => {
                        setValue("text", "");
                        void onSubmit();
                      }}
                    />
                  )
                }
              />
            )}
          />
        </S.GrowingFilterBarItem>
        <S.RangeRow>
          <S.FilterBarItem>
            <S.Label>{t`components.machine-logs.time-range`}:</S.Label>
            <Controller
              control={control}
              name="range"
              render={({ field }) => (
                <Select
                  {...field}
                  value={filterTextRange ?? field.value}
                  options={rangeOptions}
                  isClearable={false}
                  isSearchable={false}
                  onChange={(range: TimeRange | "custom") => {
                    if (range === "custom") {
                      onCustomRange();
                    } else {
                      field.onChange(range);
                      onRangeChange(range);
                    }
                  }}
                />
              )}
            />
          </S.FilterBarItem>
          <ButtonGroup>
            <Button type="submit" size="small">
              {t`components.machine-events.run-query`}
            </Button>
            <ShareLinkButton />
          </ButtonGroup>
        </S.RangeRow>
      </S.FilterBar>
    </Form>
  );
};

/**
 * Parse the user-entered filter query text into a structured query that may
 * include some or all of the following fields from `LogQuery: `from`, `to`,
 * `text`, `components`, `data`. Note that `level` and `limit` use dedicated UI
 * controls, so they are not included in the filter text parsing.
 *
 * If the input query doesn't include a particular field above, that field will
 * not be included in the output.
 *
 * Syntax looks like this: [data.foo=42] [source=agent] endstop out of bounds
 *
 * Example queries:
 * - `[since=2023-10-01] [until=2023-10-02] [source=Agent] [data.foo=42] erase`
 * - `[since=P3D] [before=P1D] [source=Agent,Klipper] [data.foo=bar,baz] tool
 *   change`
 * - `[since=2023-10-01T12:00:00Z] [data.code=foo] [data.bar=baz]`
 * - `reset mcu`
 * - `[data.foo.bar=false]`
 *
 * Queries are case-insensitive for everything. Whitespace will be trimmed from
 * the start and end of queries.
 *
 * Filters are wrapped in brackets, with name=value inside. Text not wrapped in
 * brackets will just be sent as a full-text search query to the server.
 *
 * Valid field names will be: since, until, code, source, and data.*.
 *
 * Any content inside brackets will first try parsing as `name=value`. If
 * parsing fails (e.g. name doesn't match allowed names, or value is missing, or
 * there's no equals sign) then the bracketed string (including the brackets)
 * will be included in `text`. This allows copy/pasting log lines that happen to
 * include brackets, as long as their key names don't match ours.
 *
 * Spaces around the name, value, or equals sign are trimmed before interpreting
 * them. So `[ foo = bar ]` and `[foo=bar]` are equivalent.
 *
 * Names must not contain any of the following: `'`, `"`, `=`, `,`, or any
 * whitespace character. Quoting in names not allowed (for now at least).
 *
 * Values cannot contain newlines.
 *
 * Names may be repeated in separate filters. The same name can also be used
 * with multiple, comma-separated values for names that accept multiple values.
 * For example, `[source=klipper,agent]` is equivalent to `[source=klipper]
 * [source=agent]`.
 *
 * Values that contain matched pairs of brackets are accepted, specifically to
 * handle RFC 9557 timestamps with time zone annotations. For example, this is
 * valid: [data.created=2025-01-20T00:00[America/Los_Angeles]. Values with
 * unmatched brackets will fail parsing.
 *
 * For `source`:
 * - This is called "component" in the API and server, but I think “source” is a
 *   better name for user-facing UI because it's easier to type and it matches
 *   what Windows calls this concept and almost all internal users are on
 *   Windows.
 * - Could extend this later, e.g. ! to exclude a component, but keep it simple
 *   for now.
 *
 * For `until` and `since`:
 * - The value is one of the following formats, which each correspond to a
 *   Temporal type:
 *   - `Temporal.PlainDate`: a date in ISO 8601 format (YYYY-MM-DD). The
 *     browser's local time zone will be used to convert to an epoch timestamp.
 *     If only a date is provided, then it means the first microsecond of the
 *     date for `since`, and the last microsecond for `until`.
 *   - `Temporal.PlainDateTime`: an offset-less ISO 8601 date/time string
 *     (YYYY-MM-DDThh:mm:ss.ssssss), where seconds and fractional seconds are
 *     optional. The browser's local time zone will be used to convert to an
 *     epoch timestamp.
 *   - `Temporal.Instant`: an RFC 3339 timestamp using either a numeric offset
 *     or a Z offset
 *   - `Temporal.ZonedDateTime`: an RFC 9557 timestamp including an offset
 *     and/or a time zone annotation
 *   - `Temporal.Duration`: an ISO 8601 duration string like P3D or PT12H.
 *     Durations are subtracted from the current time, so [after=P2D] means
 *     events since the current time 2 days ago.
 * - Both `until` and `since` are inclusive of the provided value.
 * - Unlike other filters, multiple values are not accepted.
 *
 * For `data`:
 * - The value is a JSONPath expression. Example: [data=$.foo.bar="hello"]
 * - Unlike other filters, data filters are case sensitive. The JSONPath
 *   expression must match the data structure exactly, including all keys and
 *   their casing.
 * - If `data` is present multiple times, each filter must be matched; the
 *   filters are AND-ed.
 *
 * For `code`:
 * - It's a shorthand for `[data=$.code=foo]`, because filtering by code will be
 *   very common. We want to encourage AON3D developers to add unique,
 *   i18n-friendly codes to events logged by AON3D components (especially for
 *   error conditions that, at some point, we want end-users to see) so this is
 *   a nice ease-of-use reward for developers who do this work.
 * - If multiple `code` filters are present, they will be OR-ed together into a
 *   single JSONPath expression. Similarly, if multiple codes are present,
 *   comma-separated, in a single `[code=...]` filter, they will also be OR-ed
 *   together.
 */
export function parseFilterText(
  text: string
): Omit<
  Partial<LogQuery>,
  "limit" | "machineIds" | "level" | "order" | "levels"
> {
  if (!text) return {};
  text = text.trim();
  if (!text) return {};

  // Regular expression to match bracketed filters. Examples:
  // - [source=klipper]
  // - [source=klipper,agent]
  // - [since=2025-01-05T12:00-07:00[America/Denver]]
  // - [until=2025-01-05]
  // - [until=2025-01-05T12:00:00Z]
  // - [until=2025-01-05T12:00:00]
  // - [code=SPI_INTERRUPTED]
  // - [code=SPI_INTERRUPTED,TIMER_TOO_CLOSE]
  // - [data=$.foo.bar==hello]
  // Note that only one level of nested brackets are allowed. [name=val[val2[2]]] will not be accepted.
  const filterRegex = /\[([^[\]=,'"\s]+)\s*=\s*((?:[^[\]]+|\[[^[\]]*\])*)\]/gs;

  // Store parsed fields
  const result: Omit<Partial<LogQuery>, "limit" | "machineIds" | "level"> = {};

  // Keep track of text outside bracketed filters
  const textParts: string[] = [];

  // Copy of input to remove bracketed filters as we parse them
  let remaining = text;

  // Helper to parse date/time/duration using Temporal
  // Returns a Temporal.Instant object if successful, or a localized error message sting if not.
  function parseTemporalValue(
    val: string,
    isUntil: boolean
  ): Temporal.Instant | false {
    // Try Temporal types in order: first Duration, then ZonedDateTime,
    // Instant, and finally PlainDateTime or PlainDate.
    val = val.trim();

    try {
      const duration = Temporal.Duration.from(val);
      // `Instant.subtract` only accepts hours or smaller units
      const hoursOrSmaller = duration.round({
        largestUnit: "hours",
        relativeTo: Temporal.Now.zonedDateTimeISO(),
      });
      return Temporal.Now.instant().subtract(hoursOrSmaller);
    } catch {
      // fall through to the next type
    }

    try {
      return Temporal.ZonedDateTime.from(val).toInstant();
    } catch {
      // fall through to the next type
    }

    try {
      return Temporal.Instant.from(val);
    } catch {
      // fall through to the next type
    }

    // If we get here, `val` (if valid) is either a date or an offset-less
    // date/time string. YYYY-MM-DD is valid to parse using PlainDate or
    // PlainDateTime, but date-only semantics are different for `until`, where a
    // date value should return the last instant of that day but
    // `PlainDateTime.from('2025-01-01')` returns the first instant of that day.
    // To tell the difference, look for a date/time separator in the string
    // followed by a digit.
    const hasTimeSeparator = /[ Tt][0-9]/;
    if (hasTimeSeparator.test(val)) {
      try {
        const pdt = Temporal.PlainDateTime.from(val);
        return pdt.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant();
      } catch {
        // fall through to the next type
      }
    }

    // The only format remaining is YYYY-MM-DD
    try {
      const pd = Temporal.PlainDate.from(val);
      let zdt = pd.toZonedDateTime(Temporal.Now.timeZoneId());
      // For `until`, use the last instant of the day. For `since`, use start of day.
      if (isUntil) {
        zdt = zdt.add({ days: 1 }).subtract({ nanoseconds: 1 });
      }
      return zdt.toInstant();
    } catch {
      return false;
    }
  }

  // Parse all bracketed filters
  let match: RegExpExecArray | null;
  while ((match = filterRegex.exec(text))) {
    const [full, nameRaw, valueRaw] = match;
    const name = nameRaw.trim().toLowerCase();
    const value = valueRaw.trim();
    // Remove this filter from remaining text
    remaining = remaining.replace(full, " ").trim();

    if (name === "until") {
      const instant = parseTemporalValue(value, true);
      if (instant) {
        result.to = instant.toString();
      }
    } else if (name === "since") {
      const instant = parseTemporalValue(value, false);
      if (instant) {
        result.from = instant.toString();
      }
    } else if (name === "source") {
      // Comma-separated list
      const components = value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      if (components.length) {
        result.components = (result.components ?? []).concat(components);
      }
    } else if (name === "code") {
      // `code` is a shorthand for [data=$.code==foo]
      const codes = value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      if (codes.length === 1) {
        // If there's only one code, use a simple equality check
        result.data = [...(result.data ?? []), `$.code=="${codes[0]}"`];
      } else if (codes.length > 1) {
        // If there are multiple codes, use a JSONPath `IN` expression
        result.data = [
          ...(result.data ?? []),
          `$.code IN (${codes.map((c) => `"${c}"`).join(", ")})`,
        ];
      }
    } else if (name === "data") {
      result.data = [...(result.data ?? []), value.trim()];
    } else {
      // If not a recognized filter, treat as text
      textParts.push(full);
    }
  }

  // Any remaining text (outside brackets) is treated as full-text search
  if (remaining.trim()) textParts.push(remaining.trim());
  if (textParts.length) result.text = textParts.join(" ").trim();

  return result;
}

/**
 * Replace any `[since=...]` and `[until=...]` filters in the filter text with
 * a relative `[since=<duration>]` filter, e.g. `[since=P1D]`, keeping all
 * other filters and search text. With no `until`, the range always ends at
 * the current time. See `parseFilterText` for the filter syntax.
 */
export function replaceTimeFilters(
  text: string,
  since: Temporal.Duration
): string {
  const otherFilters = removeTimeFilters(text);
  return `${otherFilters} [since=${since.toString()}]`.trim();
}

/**
 * Replace any `[since=...]` and `[until=...]` filters in the filter text with
 * filters for the given instants, keeping all other filters and search text.
 * The instants are written with a `Z` offset for UTC, or the browser's current
 * UTC offset for local time, e.g. `2026-10-08T09:00:00-07:00`.
 */
export function replaceTimeFiltersWithRange(
  text: string,
  since: Temporal.Instant,
  until: Temporal.Instant,
  timeZone: DisplayTimeZone
): string {
  const otherFilters = removeTimeFilters(text);
  const sinceText = instantFilterValue(since, timeZone);
  const untilText = instantFilterValue(until, timeZone);
  const timeFilters = `[since=${sinceText}] [until=${untilText}]`;
  return `${otherFilters} ${timeFilters}`.trim();
}

function instantFilterValue(
  instant: Temporal.Instant,
  timeZone: DisplayTimeZone
): string {
  return timeZone === "utc"
    ? instant.toString()
    : instant.toString({ timeZone: timeZoneId("local") });
}

// Matches `since`/`until` filters, capturing the name and value. Values may
// contain one level of nested brackets, like RFC 9557 time zone annotations.
const TIME_FILTER_REGEX =
  /\[\s*(since|until)\s*=\s*((?:[^[\]]+|\[[^[\]]*\])*)\]/gi;

function removeTimeFilters(text: string): string {
  return text.replace(TIME_FILTER_REGEX, "").trim();
}

function normalizedDuration(value: string): string | null {
  try {
    return Temporal.Duration.from(value).toString();
  } catch {
    return null;
  }
}

/**
 * Which range option describes the time filters in the filter text: the
 * matching preset when the text has a single relative `since` filter like
 * `[since=P1D]`, `null` when it has no time filters, and `"custom"` for
 * anything else, such as explicit timestamps.
 */
export function rangeOptionForFilterText(
  text: string
): TimeRange | "custom" | null {
  const filters = [...text.matchAll(TIME_FILTER_REGEX)].map((match) => ({
    name: match[1].toLowerCase(),
    value: match[2].trim(),
  }));
  if (filters.length === 0) {
    return null;
  }
  if (filters.length > 1 || filters[0].name !== "since") {
    return "custom";
  }
  const sinceDuration = normalizedDuration(filters[0].value);
  const preset = TIME_RANGES.find((range) => range === sinceDuration);
  return preset ?? "custom";
}

const FilterLink = ({
  children,
  filter,
  addFilter,
}: {
  children: ReactNode;
  filter: string;
  addFilter: (filter: string) => void;
}) => {
  return (
    <S.FilterLink
      title={t`components.machine-events.add-filter-tooltip ${filter}`}
      onClick={() => addFilter(filter)}
      role="button"
    >
      {children}
    </S.FilterLink>
  );
};

export const MachineLogs = ({ machine }: MachineLogsProps): ReactElement => (
  <MemoizedMachineLogs machineId={machine.id} />
);

// This component's parent component will re-render every 5s when polled machine
// status is updated. React components normally re-render when parents change.
// Each cell's render functions don't have stable identity because they're
// defined inline. Indeed they cannot have stable identity because they depend
// on props that the table doesn't know about, like `addFilter` and
// `displayTimeZone`. This re-rendering will cause the logs table cells to
// remount which will reset local state like the expand/collapse state of long
// "Data" cells.
//
// To avoid this, just use `React.memo` to avoid re-rendering the logs table if
// the machine ID is unchanged.
const MemoizedMachineLogs = memo(function LogsView({
  machineId,
}: {
  machineId: Uuid;
}) {
  const defaultParams: QueryParams = {
    range: "PT24H",
    text: "",
    levels: ["error", "warn", "info"] as LogLevel[],
  };

  // TODO: Use `useQueryParams` instead.
  const [qs, setQs] = useSearchParams();

  const qsText = qs.get("text") || "";
  const qsLevels = intersection(
    qs.get("levels")?.split(",") || [],
    LOG_LEVEL_NAMES
  );
  const qsRange = qs.get("range");

  const startingParams: QueryParams = {
    text: qsText.trim(),
    levels: qsLevels.length ? qsLevels : defaultParams.levels,
    range: isTimeRange(qsRange) ? qsRange : defaultParams.range,
  };

  const [defaultTo, setDefaultTo] = useState(() => Temporal.Now.instant());
  const [displayTimeZone, setDisplayTimeZone] =
    useState<DisplayTimeZone>("local");

  const [queryParams, setQueryParams] = useState<QueryParams>(startingParams);
  const [_, startTransition] = useTransition();

  // Used by links inside the table to add a filter to the query.
  const addFilter = (filterText: string) => {
    const currentText = getValues("text");
    setValue("text", `${currentText?.trim() ?? ""} ${filterText}`.trim());
    void onSubmit();
  };

  // Write a range picked in the date/time inputs into the filter text as
  // explicit `since`/`until` filters.
  const selectAbsoluteRange = (
    start: Temporal.Instant,
    end: Temporal.Instant
  ) => {
    const text = replaceTimeFiltersWithRange(
      getValues("text"),
      start,
      end,
      displayTimeZone
    );
    setValue("text", text);
    void onSubmit();
  };

  const rangeStartInputRef = useRef<HTMLInputElement>(null);

  // Freeze the current range as explicit filters so the query matches the
  // "Custom" label, then prompt the user to pick a start in the date/time
  // inputs.
  const selectCustomRange = () => {
    selectAbsoluteRange(
      Temporal.Instant.fromEpochMilliseconds(rangeStartMs),
      Temporal.Instant.fromEpochMilliseconds(rangeEndMs)
    );
    // The dropdown moves focus back to itself after a selection, so wait for
    // that before focusing the input. Browsers still allow opening the picker
    // shortly after the click that chose "Custom".
    setTimeout(() => {
      if (rangeStartInputRef.current) {
        openPicker(rangeStartInputRef.current);
      }
    }, 0);
  };

  // Write the selected range into the filter text as a relative `since`
  // filter, so the search bar always shows the time range being queried.
  const selectRange = (range: TimeRange) => {
    const since = Temporal.Duration.from(range);
    setValue("text", replaceTimeFilters(getValues("text"), since));
    void onSubmit();
  };

  // Zoom back out to the longest preset range, e.g. after dragging across
  // the chart to zoom in.
  const resetRange = () => {
    setValue("range", RESET_TIME_RANGE);
    selectRange(RESET_TIME_RANGE);
  };

  const filterText = queryParams.text ?? "";
  // Relative filters like `[since=P1D]` are resolved against the current time
  // when parsed. Each submit without an explicit `until` resets `defaultTo` to
  // now, so re-parse then too to keep both ends of the range current.
  const parsed = useMemo(
    () => parseFilterText(filterText),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [filterText, defaultTo]
  );
  // Explicit `[since=...]` / `[until=...]` filters override the time range.
  const to = parsed.to ?? defaultTo.toString();
  // Subtract the range as a zoned date/time, because `Instant#subtract` doesn't
  // accept days.
  const from =
    parsed.from ??
    Temporal.Instant.from(to)
      .toZonedDateTimeISO(Temporal.Now.timeZoneId())
      .subtract(Temporal.Duration.from(queryParams.range))
      .toInstant()
      .toString();
  const logQuery: LogQuery = {
    from,
    to,
    components: parsed.components,
    data: parsed.data,
    text: parsed.text,
    machineIds: [machineId],
    order: "desc",
    levels: queryParams.levels,
    limit: MAX_LOG_EVENTS,
  };

  const { data: events = [], isLoading, refetch } = useMachineLogs(logQuery);
  const limit = MAX_LOG_EVENTS;
  const isTruncated = events.length >= limit;
  const displayLevels = LEVEL_DISPLAY_ORDER.filter((level) =>
    queryParams.levels.includes(level)
  );
  // Epoch milliseconds, because `Date` can't reliably parse the nanosecond
  // precision that Temporal instant strings may include.
  const rangeStartMs = Temporal.Instant.from(from).epochMilliseconds;
  const rangeEndMs = Temporal.Instant.from(to).epochMilliseconds;

  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<string>("auto");

  // Set the height of the log container to fill the remaining viewport height
  // so that the table is scrollable.
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const top = containerRef.current.getBoundingClientRect().top;
        setHeight(`calc(100vh - ${top}px)`);
      }
    };

    // Run on load and resize
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // I don't love having `useForm` here instead of in <FilterBar> (where the
  // form actually is), but if we want to update the query text in response to
  // users clicking on a link in the table, then we need access to `getValues`.
  const { control, setValue, getValues, handleSubmit } = useForm({
    defaultValues: queryParams,
  });

  const onSubmit = handleSubmit((data: QueryParams) => {
    // FIXME: This isn't very performant (even though it's not a hot codepath).
    // Change this to use `contains` or just `symmetricDifference` instead.
    function haveSameValues<T>(a: T[], b: T[]) {
      return symmetricDifference(a, b).length === 0;
    }

    // Advance the default time range to now on each submit so the query covers
    // fresh data. When the filter text provides explicit from/to overrides the
    // defaults aren't used, so this has no effect in that case. Both updates
    // share a transition so the logs aren't first queried with the new
    // `defaultTo` but the old filter text.
    const newParsed = parseFilterText(data.text);
    startTransition(() => {
      setQueryParams(data);
      if (!newParsed.from || !newParsed.to) {
        setDefaultTo(Temporal.Now.instant());
      }
    });

    // When the filter text provides explicit from/to, the defaults above won't
    // change the key. If nothing else changed either, call refetch() explicitly
    // since react-query won't auto-refetch an unchanged key.
    const hasExplicitTimeRange = !!newParsed.from && !!newParsed.to;
    const paramsUnchanged =
      hasExplicitTimeRange &&
      data.text === queryParams.text &&
      data.range === queryParams.range &&
      haveSameValues(data.levels, queryParams.levels);
    if (paramsUnchanged) {
      void refetch();
    }

    const urlParams = new URLSearchParams();

    // Record non-default values in the URL query string
    if (data.text) {
      urlParams.set("text", data.text);
    }
    if (!haveSameValues(data.levels, defaultParams.levels)) {
      urlParams.set("levels", data.levels.join(","));
    }
    // Record the range the query actually uses: a preset named by the filter
    // text wins over the dropdown's stored value, and custom ranges are fully
    // described by the filter text.
    const textRange = rangeOptionForFilterText(data.text);
    const queriedRange = textRange ?? data.range;
    if (queriedRange !== "custom" && queriedRange !== defaultParams.range) {
      urlParams.set("range", queriedRange);
    }

    // Replace rather than push, so the browser's Back button leaves the page
    // instead of stepping through earlier searches, which this page doesn't
    // restore from the URL.
    setQs(urlParams, { replace: true });
  });

  const tableColumns: ColumnDef<LogEvent>[] = [
    {
      header: t`common.level`,
      accessorKey: "level",
      cell: ({ row: { original: rowData } }) => (
        <S.LevelCell>
          <RenderLevel event={rowData} addFilter={addFilter} />
        </S.LevelCell>
      ),
    },
    {
      header:
        displayTimeZone === "utc"
          ? t`components.machine-logs.event-time-hover-local`
          : t`common.event-time`,
      accessorKey: "timestamp",
      cell: ({ row: { original: rowData } }) => (
        <RenderTimestamp event={rowData} displayTimeZone={displayTimeZone} />
      ),
    },
    {
      header: t`common.source`,
      accessorKey: "component",
      meta: {
        // Add a more space around the source column because the previous
        // column's contents are all the same width, which makes them crowd the
        // starting edge of this column.
        variant: "normal",
      },
      cell: ({ row: { original: rowData } }) => (
        <RenderSource event={rowData} addFilter={addFilter} />
      ),
    },
    {
      header: t`common.message`,
      accessorKey: "message",
      cell: ({ row: { original: rowData } }) => (
        <RenderMessage event={rowData} addFilter={addFilter} />
      ),
    },
    {
      header: t`common.data`,
      accessorKey: "data",
      cell: ({ row: { original: rowData } }) => (
        <RenderData event={rowData} addFilter={addFilter} />
      ),
    },
  ];

  return (
    <S.Wrapper>
      <FilterBar
        control={control}
        setValue={setValue}
        filterTextRange={rangeOptionForFilterText(filterText)}
        onSubmit={onSubmit}
        onRangeChange={selectRange}
        onCustomRange={selectCustomRange}
      />
      <S.ChartSection>
        <S.ChartHeader>
          <LogLevelCounts events={events} levels={displayLevels} />
          <RangeInputs
            startMs={rangeStartMs}
            endMs={rangeEndMs}
            displayTimeZone={displayTimeZone}
            startInputRef={rangeStartInputRef}
            onRangeChange={selectAbsoluteRange}
            onDisplayTimeZoneChange={setDisplayTimeZone}
          />
        </S.ChartHeader>
        <LogChart
          events={events}
          levels={displayLevels}
          from={from}
          to={to}
          displayTimeZone={displayTimeZone}
          onRangeSelect={selectAbsoluteRange}
          onReset={resetRange}
        />
        {isTruncated && (
          <S.TruncatedNotice>
            {t`components.machine-logs.truncated ${limit}`}
          </S.TruncatedNotice>
        )}
      </S.ChartSection>
      <S.Content ref={containerRef} style={{ height }}>
        <Table
          columns={tableColumns}
          data={events}
          loading={isLoading}
          customCellCss={S.customCellCss}
          freezeHeaders
          narrowColumns
        />
      </S.Content>
    </S.Wrapper>
  );
});
